export type ChallengeDeadlinePhase = 'submission' | 'evaluation' | 'ended';

export interface ChallengeDeadlineInput {
  submissionEndsAt?: string;
  evaluationEndsAt?: string;
}

export function parseChallengeEndTime(dateStr: string): number {
  const trimmed = dateStr.trim();
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  return isDateOnly
    ? new Date(`${trimmed}T23:59:59.999`).getTime()
    : new Date(dateStr).getTime();
}

export function getChallengeDeadlinePhase(input: ChallengeDeadlineInput): ChallengeDeadlinePhase {
  const now = Date.now();
  const submissionEnd = input.submissionEndsAt?.trim();
  if (submissionEnd && parseChallengeEndTime(submissionEnd) > now) {
    return 'submission';
  }
  const evaluationEnd = input.evaluationEndsAt?.trim();
  if (evaluationEnd && parseChallengeEndTime(evaluationEnd) > now) {
    return 'evaluation';
  }
  return 'ended';
}

export function getActiveChallengeDeadlineEnd(input: ChallengeDeadlineInput): string | undefined {
  const phase = getChallengeDeadlinePhase(input);
  if (phase === 'submission') {
    return input.submissionEndsAt;
  }
  if (phase === 'evaluation') {
    return input.evaluationEndsAt;
  }
  return undefined;
}

export function getChallengeHoursLeft(endDate: string): number | null {
  const endTime = parseChallengeEndTime(endDate);
  const now = Date.now();
  if (endTime <= now) {
    return null;
  }
  return Math.ceil((endTime - now) / (1000 * 60 * 60));
}

export function computeChallengeDaysRemaining(endDate: string): number {
  const endTime = parseChallengeEndTime(endDate);
  const now = Date.now();
  if (endTime <= now) {
    return -1;
  }
  return Math.ceil((endTime - now) / (1000 * 60 * 60 * 24));
}

export function formatChallengeDeadlineLabel(input: ChallengeDeadlineInput): string {
  const activeEnd = getActiveChallengeDeadlineEnd(input);
  if (!activeEnd) {
    return 'Ended';
  }

  const hours = getChallengeHoursLeft(activeEnd);
  if (hours != null && hours > 0 && hours < 24) {
    return hours === 1 ? '1hr left' : `${hours}hr left`;
  }

  const days = computeChallengeDaysRemaining(activeEnd);
  if (days < 0) {
    return 'Ended';
  }
  if (days === 0) {
    return parseChallengeEndTime(activeEnd) > Date.now() ? 'Deadline today' : 'Ended';
  }
  if (days === 1) {
    return '1 day left';
  }
  return `${days} days left`;
}

export function formatChallengeDeadlineTooltip(
  input: ChallengeDeadlineInput,
  formatDate: (dateStr: string) => string,
): string {
  const phase = getChallengeDeadlinePhase(input);
  if (phase === 'submission' && input.submissionEndsAt) {
    return `Submission: ${formatDate(input.submissionEndsAt)}`;
  }
  if (phase === 'evaluation' && input.evaluationEndsAt) {
    return `Evaluation: ${formatDate(input.evaluationEndsAt)}`;
  }
  return 'This challenge has ended';
}

export function isChallengeDeadlineUrgent(input: ChallengeDeadlineInput): boolean {
  const activeEnd = getActiveChallengeDeadlineEnd(input);
  if (!activeEnd) {
    return false;
  }
  const hours = getChallengeHoursLeft(activeEnd);
  return hours != null && hours > 0 && hours < 24;
}
